import config from "../config";
import util from './utils';
import lngSwitch from "./languageSwitching";

// 定数
const status_approved = 'state_pfc_detail_table_approvaled';
const status_unapproved = 'state_pfc_detail_table_not_approvaled';

// グローバル変数
let groupList = [];
let areaList = [];

/* **
 * 仕向け地一覧およびエリア一覧を生成。
 * @param approvals: 承認依頼を出すことができる国の一覧
 * @param groupIds: サービス内で利用可能な仕向け地の一覧
 * @param areaIds: サービス内で利用可能なエリアの一覧
 */
export const computeRegionAndArea = (approvals, groupIds, areaIds) => {
  groupList = [];
  areaList = [];
  const tmpGroups = [];
  const tmpAreas = [];

  const createName = function (country, code) {
    let name = '';
    name = name + (country ? country : '');
    name = name + (code ? `(${code})` : '');
    return name;
  };

  approvals.sort((a, b) => {
    if (a.group.toUpperCase() > b.group.toUpperCase()) return 1;
    if (a.group.toUpperCase() < b.group.toUpperCase()) return -1;
    return 0;
  });
  // 承認依頼先会社の一覧から、重複のないグループとエリアの一覧を作成
  approvals.forEach((el) => {
    if (tmpGroups.indexOf(el.group) === -1) tmpGroups.push(el.group);
    if (tmpAreas.indexOf(el.area) === -1) tmpAreas.push(el.area);
  });
  // 上記の一覧の内、それぞれの要素が想定されるグループまたはエリアであるか判定し、
  // YESなら画面表示用リスト変数にストック
  tmpGroups.forEach((group) => {
    groupIds.forEach((g) => {
      if (group === g.value)
        groupList.push({ value: group, text: g.id });
    });
  });
  tmpAreas.forEach((el) => {
    areaIds.forEach((a) => {
      if (el === a.value)
        areaList.push({ value: el, text: a.id, properties: [] });
    });
  });
  approvals.forEach((approval) => {
    areaList.forEach((a) => {
      if (approval.area === a.value)
        a.properties.push({
          name: createName(approval.country, approval.code),
          countryCode: approval.country,
          code: approval.code,
          group: approval.group,
          status: approval.is_approval,
          isAlreadyApproved: approval.approval_status === 'approved' ? true : false
        });
    });
  });
};

/* **
 * 仕向け地一覧を取得。
 * @return 仕向け地一覧のリスト
 */
export const getGroupList = () => groupList;

/* **
 * エリア一覧を取得。
 * @return エリア一覧のリスト
 */
export const getAreaList = () => areaList;

/**
 * 承認状況テーブルを UI 表示用に2分割して返す。
 * @param array 分割したい配列
 * @return 分割した2つの配列
 */
export const devideArray = (array) => {
  const wholeElems = [];
  for (const i in array) {
    wholeElems.push({
      code: array[i].id,
      countryCode: array[i].country_code,
      approval: array[i].status === 'approved' ? status_approved : status_unapproved,
      marked: array[i].status === 'unapproved'
    });
  }
  const divIndex = Math.ceil(wholeElems.length / 2);
  const divElems = [];
  divElems.push(wholeElems.slice(0, divIndex));
  divElems.push(wholeElems.slice(divIndex));
  return divElems;
};

/**
 * 画面に表示する国名をメンテナンス設定から逆引きして返す。
 * @param prop 国コードと会社コード
 * @param list メンテナンス機能で設定されている国一覧
 * @param lang 表示する言語
 * @return 表示内容
 */
export const getDisplayName = (prop, list, lang) => {
  let name = '';
  if (prop.countryCode) {
    const filteredList = list.filter(item => item.id === prop.countryCode);
    if (filteredList.length > 0) {
      const displayName = lang === 'ja' ? filteredList[0].japanese_name : filteredList[0].english_name;
      name = name + (displayName ? displayName : '- ');
    }
  } else {
    name = '- ';
  }
  name = name + (prop.code ? `(${prop.code})` : '');
  return name;
};

/**
 * 翻訳言語ラジオの表示情報を生成する。
 * @param {string} displayLang 画面表示言語
 * @param {Array} translations 翻訳設定配列
 * @param {Array} translationLangs 利用可能な翻訳言語定義
 * @returns {Array<{value:string,label:string,isDefault:boolean}>}
 */
export const updateTranslationRadios = (displayLang, translations, translationLangs) => {
  const radios = [];
  if (!translations || translations.length < 1) return radios;
  for (const translation of translations) {
    const index = translationLangs.findIndex((lang) => lang.id === translation.language);
    if (index !== -1) {
      radios.push({
        value: translation.language,
        label: translationLangs[index].display[displayLang],
        isDefault: translation.is_default
      });
    }
  }
  return radios;
};

/**
 * 承認ステータスの更新ログ表示情報を生成する。
 * @param {Object} arg 承認情報
 * @returns {{approvedTime:string,approvedUser:string,showCanceledLog:boolean}}
 */
export const computeReleaseLogs = (arg) => {
  const ret = {
    approvedTime: '',
    approvedUser: '',
    showCanceledLog: arg.status !== config.PFC_STATUS_APPROVALED && arg.unapproved_time
  };
  if (ret.showCanceledLog) {
    ret.approvedTime = arg.unapproved_time ? util.timeConverter(arg.unapproved_time) : '';
    ret.approvedUser = arg.unapproved_by;
  } else {
    ret.approvedTime = arg.approved_time ? util.timeConverter(arg.approved_time) : '';
    ret.approvedUser = arg.approved_by;
  }
  return ret;
};

/**
 * 翻訳一覧から表示対象の言語の情報を抽出する。
 * @param translationList 翻訳一覧
 * @param selectedLanguage 現在選択中の言語
 * @returns 解決結果
 */
export const resolveTranslation = (translationList, selectedLanguage = 'en') => {
  const list = Array.isArray(translationList) ? translationList : [];
  if (list.length === 0) {
    return {
      selectedLanguage: null,
      selectedTranslation: null,
      selectedIndex: -1
    };
  }

  // 選択中の言語に一致する翻訳言語を検索
  let index = list.findIndex((item) => item.language === selectedLanguage);
  // 一致する翻訳言語がない場合、デフォルトの翻訳言語を検索
  if (index === -1) {
    index = list.findIndex((item) => item.is_default === true);
  }
  // デフォルトの翻訳言語もない場合、先頭の翻訳言語を選択
  if (index === -1) {
    index = 0;
  }

  return {
    selectedLanguage: list[index].language,
    selectedTranslation: list[index],
    selectedIndex: index
  };
};

/**
 * サービスニュース URL の末尾スラッシュを除去する。
 * @param baseUrl サービスニュース URL
 * @returns 正規化した URL
 */
export const normalizeServiceNewsBaseUrl = (baseUrl) => {
  if (!baseUrl || typeof baseUrl !== 'string') {
    return '';
  }
  return baseUrl.replace(/\/+$/, '');
};

/**
 * 文字列が http/https URL 形式か判定する。
 * @param value 判定対象
 * @returns true: URL / false: 非URL
 */
export const isHttpUrl = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  return config.URL_PATTERN.test(value.trim());
};

/**
 * URL 文字列から表示名を抽出する。
 * @param value URL 文字列
 * @returns 表示名
 */
export const extractServiceNewsNameFromUrl = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  let base = value;
  // クエリパラメータを除去
  const queryIndex = base.indexOf('?');
  if (queryIndex !== -1) {
    base = base.substring(0, queryIndex);
  }
  // "/"でパスを分割して、最後の非空要素を表示名とする
  const pathNames = base.split('/');
  for (let i = pathNames.length - 1; i >= 0; --i) {
    if (pathNames[i]) {
      return pathNames[i];
    }
  }
  return '';
};

/**
 * サービスニュース表示用リンク一覧を生成する。
 * @param serviceNewsUrl サービスニュース URL
 * @param serviceNewsName サービスニュース名配列
 * @returns 整形結果
 */
export const buildServiceNewsList = (serviceNewsUrl, serviceNewsName) => {
  const base = normalizeServiceNewsBaseUrl(serviceNewsUrl);
  const names = Array.isArray(serviceNewsName) ? serviceNewsName : [];
  const separator = lngSwitch.getServiceNewsSeparator();

  const serviceNewsList = [];
  names.forEach((item) => {
    const name = (item || '').toString();
    // URL_PATTERNの形式を満たす場合のみ、"base"との合成を実施しない
    if (isHttpUrl(name)) {
      serviceNewsList.push({
        name: extractServiceNewsNameFromUrl(name),
        url: name
      });
      return;
    }
    // URL_PATTERNの形式を満たさない場合は、"base"との合成を実施する
    serviceNewsList.push({
      name: name,
      url: base ? `${base}/${name}` : null
    });
  });

  return {
    serviceNewsUrl: base,
    serviceNewsList: serviceNewsList,
    serviceNewsText: serviceNewsList.map((item) => item.name).join(separator)
  };
};

/**
 * 翻訳一覧から画面表示に必要な値を組み立てる。
 * @param translationList 翻訳一覧
 * @param selectedLanguage 選択中の言語
 * @returns 組み立て結果
 */
export const buildTranslationDisplay = (translationList, selectedLanguage = 'en') => {
  const resolved = resolveTranslation(translationList, selectedLanguage);
  const selected = resolved.selectedTranslation;

  if (!selected) {
    return {
      selectedLanguage: resolved.selectedLanguage,
      selectedTranslation: null,
      serviceNewsUrl: '',
      serviceNewsList: [],
      serviceNewsText: ''
    };
  }

  const serviceNews = buildServiceNewsList(
    selected.service_news_url,
    selected.service_news_name
  );

  return {
    selectedLanguage: resolved.selectedLanguage,
    selectedTranslation: selected,
    serviceNewsUrl: serviceNews.serviceNewsUrl,
    serviceNewsList: serviceNews.serviceNewsList,
    serviceNewsText: serviceNews.serviceNewsText
  };
};

/**
 * 承認更新API向けのリクエストボディを生成する。
 * @param checkedCountries 選択済み承認国
 * @param lastUpdateTime 最終更新日時
 * @return API送信用ボディ
 */
export const getTargetApprovals = (checkedCountries, lastUpdateTime) => {
  const result = { approval_ids: [], last_update_time: null };
  checkedCountries.forEach((el) => {
    result.approval_ids.push(el.code);
  });
  result.last_update_time = lastUpdateTime;
  return result;
};

/**
 * 仕向が国内かどうかを判定する。
 * @param companies 承認依頼会社一覧
 * @return 国内仕向が含まれている場合 true
 */
export const checkDomestic = (companies) => {
  let result = false;
  for (const i in companies) {
    if (companies[i].group === config.REGION_DOMESTIC) result = true;
  }
  return result;
};

/**
 * 選択済み承認国の表示スタイルを返す。
 * @param checkedCountries 選択済み承認国
 * @param val 判定対象の国情報
 * @return スタイル文字列
 */
export const getSelectedStyle = (checkedCountries, val) => {
  for (const el in checkedCountries) {
    if (checkedCountries[el].code === val.code)
      return 'background-color: rgba(243, 246, 250, 1); color: rgba(41, 133, 219, 1);';
  }
  return '';
};

export default {
  computeRegionAndArea,
  getGroupList,
  getAreaList,
  devideArray,
  getDisplayName,
  updateTranslationRadios,
  computeReleaseLogs,
  resolveTranslation,
  normalizeServiceNewsBaseUrl,
  isHttpUrl,
  extractServiceNewsNameFromUrl,
  buildServiceNewsList,
  buildTranslationDisplay,
  getTargetApprovals,
  checkDomestic,
  getSelectedStyle
};